/** Files out of a paste. A screenshot pasted from the clipboard arrives as a
 *  File called `image.png` (or with no name at all) — renamed to a
 *  timestamped `pasted-…` so two pastes are told apart in the chat and in
 *  the Evidence list. */

const EXT_BY_TYPE: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

/** Browsers' generic names for clipboard images. */
const GENERIC_NAME = /^(image|blob|clipboard)(\.(png|jpe?g|gif|webp))?$/i;

function stamp(now: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return (
    `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}-` +
    `${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`
  );
}

export function renamePasted(file: File, index = 0, now = new Date()): File {
  if (file.name && !GENERIC_NAME.test(file.name)) return file;
  const ext = EXT_BY_TYPE[file.type] ?? (file.name.split('.').pop() || 'bin');
  const suffix = index > 0 ? `-${index + 1}` : '';
  return new File([file], `pasted-${stamp(now)}${suffix}.${ext}`, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

/** Every file on the clipboard (files list first, then file items — some
 *  browsers only expose a pasted screenshot as an item). */
export function clipboardFiles(data: DataTransfer | null, now = new Date()): File[] {
  if (!data) return [];
  let files = Array.from(data.files ?? []);
  if (files.length === 0 && data.items) {
    files = Array.from(data.items)
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter((f): f is File => f != null);
  }
  return files.map((f, i) => renamePasted(f, i, now));
}

/** True when the paste also carries text — then the text pastes normally
 *  into the question and only the files are attached. */
export function hasPlainText(data: DataTransfer | null): boolean {
  return Array.from(data?.types ?? []).includes('text/plain');
}
